import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, pairwise } from 'rxjs';
import {
  DeviceErrorHelperModelBase,
  DeviceErrorHelperStep,
  GAP_BETWEEN_STEPS,
  PADDING_X,
  STEP_WIDTH,
} from 'src/app/common/utils/device-error-helper/device-error-helper-model-base';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { UiService } from 'src/app/services/ui.service';
import { PencilButtonThemeEnum } from 'src/app/standalones/components/pencil-button/pencil-button.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';

const DEVICE_ORIENTATION_DELAY = 200;

export enum GUIDES {
  NO_INPUT = 'no-input',
  NOT_FOUND = 'not-found',
  PERMISSION_DENIED = 'permission-denied',
  PERMISSION_DENIED_BY_SYSTEM = 'permission-denied-by-system',
  SPEAKER_SYSTEM_OUTPUT = 'speaker-system-output',
}

@UntilDestroy()
@Component({
  selector: 'app-steps-guide',
  templateUrl: './steps-guide.component.html',
  styleUrls: ['./steps-guide.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepsGuideComponent implements OnInit {
  @Input() guide!: DeviceErrorHelperModelBase;
  @Input() loading = false;
  @Input() hasRelatedError: boolean = false;
  @Input() errorMsg: string = '';

  @Output() closeModal: EventEmitter<void> = new EventEmitter();
  @Output() done: EventEmitter<void> = new EventEmitter();

  steps: DeviceErrorHelperStep[] = [];
  mobileTitle = '';
  desktopTitle = '';

  @ViewChild('slider') slider: ElementRef | undefined;
  sliderActiveIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  isMobile$ = this.uiService.isMobile;
  isPhysicalDesktop = this.deviceAndBrowserDetectorService.isDesktop();
  isiOSiPhone = this.deviceAndBrowserDetectorService.isiOSiPhone();
  isFirefox = this.deviceAndBrowserDetectorService.isFirefox();

  domListener = this.domListenerFactoryService.createInstance();
  showStepperModal$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  // Used to support steps scrolling on touch devices
  touchStartX = 0;
  distanceX = 0;

  // used to show refresh button only in permission denied guide
  // when:
  // is android
  // is iphone and in last step
  // Physical desktop and is firefox
  // tablet device and fit all steps
  showRefreshButton = false;

  showDeviceSettingsButton = false;
  deviceSettingsButtonLabel = '';

  /** ngx-translate key: desktop uses "Open System Settings", phones/tablets use "Open Settings". */
  deviceSettingsButtonLabelKey = 'Open System Settings';

  PencilButtonThemeEnum = PencilButtonThemeEnum;
  IconTypes = IconTypes;

  isLastStepVisible$ = combineLatest([
    this.showStepperModal$.pipe(map((val) => !val)),
    this.sliderActiveIndex$,
  ]).pipe(
    untilDestroyed(this),
    map(([fitAllSteps, sliderActiveIndex]) => {
      if (!this.steps?.length) {
        return false;
      }

      return fitAllSteps || sliderActiveIndex === this.steps.length - 1;
    }),
    distinctUntilChanged(),
  );

  readonly STEP_WIDTH = STEP_WIDTH;
  readonly GAP_BETWEEN_STEPS = GAP_BETWEEN_STEPS;
  readonly PADDING_X_WIDE_STEPPER = PADDING_X;
  readonly PADDING_X_BOT_SHEET = 24;

  constructor(
    private uiService: UiService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private domListenerFactoryService: DomListenerFactoryService,
  ) {
    this.uiService.isMobile.pipe(pairwise(), untilDestroyed(this)).subscribe(([prev, curr]) => {
      if (prev && !curr) {
        this.resetSliderState();
      }
    });
  }

  ngOnInit(): void {
    this.showRefreshButton = this.guide.showRefreshButton();
    this.steps = this.guide.getSteps();
    this.mobileTitle = this.guide.getMobileTitle();
    this.desktopTitle = this.guide.getDesktopTitle();
    this.showDeviceSettingsButton = this.guide.showDeviceSettingsButton();
    this.deviceSettingsButtonLabel = this.guide.getDeviceSettingsButtonLabel();

    this.showStepsAccordingToScreenSize();
    this.setupJoinCallModalResizeListener();
  }

  private setupJoinCallModalResizeListener() {
    this.domListener.add('window', 'resize', () => {
      this.resizeAllowedAccessModal();
    });
  }

  private resizeAllowedAccessModal() {
    // Used setTimeout as in case of iPad/Tablet, window.innerWidth won't reflect the new width after orienting
    // It's due to window width is updated after the orienation animation is done
    modifiedSetTimeout(() => {
      this.showStepsAccordingToScreenSize();
    }, DEVICE_ORIENTATION_DELAY);
  }

  private showStepsAccordingToScreenSize() {
    this.steps = this.guide.getSteps();
    this.mobileTitle = this.guide.getMobileTitle();
    this.desktopTitle = this.guide.getDesktopTitle();
    this.deviceSettingsButtonLabel = this.guide.getDeviceSettingsButtonLabel();

    if (this.guide.showStepperModal()) {
      this.showStepperModal$.next(true);
      return;
    }
    if (this.showStepperModal$.getValue()) {
      this.resetSliderState();
    }
    this.showStepperModal$.next(false);
  }

  ngOnDestroy(): void {
    this.domListener.clear();
  }

  onTouchStart(event: TouchEvent) {
    this.touchStartX = event.changedTouches[0].clientX;
  }

  onTouchMove(event: TouchEvent) {
    const touchX = event.changedTouches[0].clientX;
    this.distanceX = touchX - this.touchStartX;
  }

  onTouchEnd(event: TouchEvent) {
    this.touchStartX = 0;
    if (Math.abs(this.distanceX) > 50) {
      event.preventDefault(); // Prevent vertical scrolling
    }

    if (this.distanceX > 0) {
      // Swipe right
      this.backHelperLayout();
    } else if (this.distanceX < 0) {
      // Swipe left
      this.nextHelperLayout();
    }
    this.distanceX = 0;
  }

  showSpecificHelperLayout(index: number) {
    if (this.slider) {
      this.sliderActiveIndex$.next(index);
      const slideWidth = this.slider?.nativeElement.offsetWidth;
      const transform = `translateX(-${slideWidth * index}px)`;
      this.slider.nativeElement.scrollLeft = slideWidth * index;
      this.slider.nativeElement.style.transform = transform;
    }
  }

  nextHelperLayout() {
    if (this.sliderActiveIndex$.getValue() < this.steps.length - 1) {
      this.showSpecificHelperLayout(this.sliderActiveIndex$.getValue() + 1);
    }
  }

  backHelperLayout() {
    if (this.sliderActiveIndex$.getValue() > 0) {
      this.showSpecificHelperLayout(this.sliderActiveIndex$.getValue() - 1);
    }
  }

  onDone() {
    this.done.emit();
  }

  onClose() {
    this.closeModal.emit();
  }

  openDeviceSettings() {
    window.open(this.guide.deviceSettingButtonSrc(), '_blank');
  }

  private resetSliderState() {
    if (this.slider) {
      this.slider.nativeElement.style.transform = 'unset';
      this.sliderActiveIndex$.next(0);
    }
  }

  refresh() {
    window.location.reload();
  }
}
